import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../constants/api';
import { usePlan } from '../../contexts/PlanContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Shown when a plan downgrade (or expiration) leaves the account with more
 * active profiles than the new plan allows — see requiresProfileSelection
 * in PlanContext. By explicit product decision this never auto-picks for
 * the user: the account owner chooses which profiles stay public, so
 * nothing they didn't choose to hide disappears without warning.
 *
 * No close/dismiss action on purpose — this stays until resolved, mounted
 * globally (see App.js) so it follows the user across the dashboard
 * instead of only showing on the Profiles tab. The only way out besides
 * resolving it is "Cerrar sesión", so a backend failure never turns into
 * a full, unrecoverable site lockout with no escape at all.
 */
const ProfileSelectionModal = () => {
  const { requiresProfileSelection, profileLimit, refreshPlan } = usePlan();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [profiles, setProfiles] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!requiresProfileSelection) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/profiles/me`);
        if (cancelled) return;
        const list = res.data || [];
        setProfiles(list);
        // Pre-select whatever is already active, up to the limit, so the
        // common case (nothing to change but confirm) is a single click.
        const activeIds = list.filter((p) => p.is_active !== false).map((p) => p.id);
        setSelected(activeIds.slice(0, profileLimit));
      } catch (err) {
        setError(t('profileSelection.loadError', 'No pudimos cargar tus perfiles. Intenta recargar la página.'));
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [requiresProfileSelection]);

  if (!requiresProfileSelection) return null;

  const toggle = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= profileLimit) return prev;
      return [...prev, id];
    });
  };

  const handleSubmit = async () => {
    if (selected.length === 0) {
      setError(t('profileSelection.chooseAtLeastOne', 'Elige al menos un perfil para mantener activo.'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/profiles/select-active`,
        { keep_profile_ids: selected },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshPlan();
      window.location.reload(); // simplest way to get every dashboard panel (profiles list, current profile) back in sync
    } catch (err) {
      setError(err?.response?.data?.detail || t('profileSelection.saveError', 'No pudimos guardar tu selección. Intenta de nuevo.'));
      setSubmitting(false);
      // Self-healing for the rare case the write actually went through on
      // the backend but the response never made it back (timeout, dropped
      // connection): re-checking here means requiresProfileSelection can
      // still clear on its own instead of only ever clearing on success.
      refreshPlan();
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)' }}
    >
      <div
        className="bg-white max-w-md w-full overflow-hidden"
        style={{ borderRadius: 20, boxShadow: '0 24px 60px rgba(0,0,0,0.18)' }}
      >
        <div className="px-6 pt-6 pb-5" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <div
            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider"
            style={{ background: 'rgba(0,113,227,0.1)', color: '#0071e3' }}
          >
            {t('profileSelection.actionRequired', 'Acción requerida')}
          </div>
          <h2 className="text-xl font-semibold mt-3" style={{ color: '#1d1d1f', letterSpacing: '-0.02em' }}>
            {t('profileSelection.title', 'Elige qué perfiles mantener activos')}
          </h2>
          <p className="text-sm mt-2 leading-relaxed" style={{ color: '#6e6e73' }}>
            {t('profileSelection.planAllows', 'Tu plan actual permite hasta {n} {noun}.').replace('{n}', profileLimit).replace('{noun}', profileLimit === 1 ? t('profileSelection.activeProfileSingular', 'perfil activo') : t('profileSelection.activeProfilePlural', 'perfiles activos'))}
            {' '}{t('profileSelection.restStayHidden', 'Los que no elijas seguirán guardados, pero dejarán de ser públicos hasta que los actives de nuevo.')}
          </p>
        </div>

        <div className="p-6">
          {loading ? (
            <p className="text-sm text-center py-6" style={{ color: '#6e6e73' }}>{t('profileSelection.loading', 'Cargando tus perfiles…')}</p>
          ) : (
            <div className="space-y-2 mb-4" style={{ maxHeight: 280, overflowY: 'auto' }}>
              {profiles.map((p) => {
                const checked = selected.includes(p.id);
                const disabled = !checked && selected.length >= profileLimit;
                return (
                  <label
                    key={p.id}
                    className="flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer"
                    style={{
                      background: checked ? 'rgba(0,113,227,0.08)' : '#f5f5f7',
                      border: checked ? '1px solid rgba(0,113,227,0.4)' : '1px solid transparent',
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(p.id)}
                      style={{ width: 16, height: 16, accentColor: '#0071e3' }}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate" style={{ color: '#1d1d1f' }}>{p.name}</div>
                      <div className="text-xs truncate" style={{ color: '#a1a1a6' }}>/{p.slug}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          <div className="text-xs mb-4" style={{ color: '#a1a1a6' }}>
            {t('profileSelection.selectedCount', '{selected} de {total} seleccionados').replace('{selected}', selected.length).replace('{total}', profileLimit)}
          </div>

          {error && (
            <div className="text-sm mb-4 px-3 py-2 rounded-lg" style={{ background: '#fef2f2', color: '#991b1b' }}>
              {error}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={submitting || loading}
            className="w-full px-4 py-3 rounded-full font-medium text-white transition-all hover:opacity-90 disabled:opacity-60"
            style={{ background: '#0071e3' }}
          >
            {submitting ? t('profileSelection.saving', 'Guardando…') : t('profileSelection.confirmSelection', 'Confirmar selección')}
          </button>

          <button
            onClick={handleLogout}
            className="w-full text-center text-xs mt-4 hover:underline"
            style={{ color: '#a1a1a6', background: 'none', border: 'none' }}
          >
            {t('profileSelection.logout', 'Cerrar sesión')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileSelectionModal;
